import React, { ButtonHTMLAttributes } from 'react';
import { buttons, animations, cx } from '../../styles';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'icon' | 'small' | 'medium' | 'large';
  shape?: 'pill' | 'rounded' | 'roundedLg';
  fullWidth?: boolean;
  className?: string;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

/**
 * Button component with consistent styling and multiple variants
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  shape = 'pill',
  fullWidth = false,
  className = '',
  disabled = false,
  isLoading = false,
  icon,
  iconPosition = 'left',
  children,
  ...props
}) => {
  const baseStyles = cx(
    // Base button styles
    'inline-flex items-center justify-center',
    animations.transition.color,
    
    // Get variant style
    disabled ? buttons.disabled : buttons[variant],
    
    // Get size style
    buttons.sizes[size],
    
    // Get shape style
    buttons.shapes[shape],
    
    // Full width
    fullWidth ? 'w-full' : '',
    
    // Loading state
    isLoading ? 'opacity-80 cursor-wait' : '',
    
    // Additional classes
    className
  );

  return (
    <button
      className={baseStyles}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className="mr-2 inline-block animate-spin">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      )}
      
      {icon && iconPosition === 'left' && !isLoading && (
        <span className="mr-1.5">{icon}</span>
      )}
      
      {children}
      
      {icon && iconPosition === 'right' && (
        <span className="ml-1.5">{icon}</span>
      )}
    </button>
  );
};

/**
 * IconButton - for buttons with just an icon and no text
 */
export const IconButton: React.FC<Omit<ButtonProps, 'size' | 'iconPosition' | 'icon'> & {
  size?: 'small' | 'medium' | 'large';
  title: string;
  children: React.ReactNode;
}> = ({
  variant = 'primary',
  shape = 'pill',
  size = 'medium',
  className = '',
  disabled = false,
  title,
  children,
  ...props
}) => {
  const sizeClasses = {
    small: 'p-1',
    medium: 'p-2',
    large: 'p-3',
  };
  
  const baseStyles = cx(
    // Base button styles
    animations.transition.color,
    
    // Get variant style
    disabled ? buttons.disabled : buttons[variant],
    
    // Get shape style
    buttons.shapes[shape],
    
    // Size
    sizeClasses[size],
    
    // Additional classes
    className
  );

  return (
    <button
      className={baseStyles}
      disabled={disabled}
      title={title}
      aria-label={title}
      {...props}
    >
      {children}
    </button>
  );
};
